import React from 'react';
import { Users, Star } from 'lucide-react';

interface Player {
  id: string;
  nome: string;
  posicao: string;
  nivel: number;
}

interface PlayerListProps {
  title: string;
  players: Player[];
  onDragStart: (e: React.DragEvent, player: Player) => void;
  emptyMessage?: string;
  showDropZone?: boolean;
  onDrop?: (e: React.DragEvent) => void;
}

const PlayerList: React.FC<PlayerListProps> = ({
  title,
  players,
  onDragStart,
  emptyMessage = "Nenhum jogador disponível",
  showDropZone = false,
  onDrop
}) => {
  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
  };

  const getPositionColor = (position: string) => {
    switch (position) {
      case 'goleiro':
        return 'bg-yellow-100 dark:bg-yellow-900/20 text-yellow-600 dark:text-yellow-400';
      case 'zagueiro':
        return 'bg-blue-100 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400';
      case 'lateral':
        return 'bg-purple-100 dark:bg-purple-900/20 text-purple-600 dark:text-purple-400';
      case 'meio-campo':
        return 'bg-green-100 dark:bg-green-900/20 text-green-600 dark:text-green-400';
      case 'atacante':
        return 'bg-red-100 dark:bg-red-900/20 text-red-600 dark:text-red-400';
      default:
        return 'bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400';
    }
  };

  return (
    <div className="card p-6">
      <h2 className="text-lg font-bold mb-4">{title}</h2>
      
      <div
        className={`${showDropZone ? 'min-h-[300px] p-4 border-2 border-dashed border-gray-300 dark:border-dark-border rounded-lg' : ''}`}
        onDrop={onDrop}
        onDragOver={showDropZone ? handleDragOver : undefined}
      >
        {players.length === 0 ? (
          <div className="text-center py-8">
            <Users className="w-12 h-12 text-gray-400 mx-auto mb-4" />
            <p className="text-gray-500 dark:text-gray-400 text-sm">
              {emptyMessage}
            </p>
            {showDropZone && (
              <p className="text-xs text-gray-400 mt-2">
                Arraste jogadores aqui para o banco de reservas
              </p>
            )}
          </div>
        ) : (
          <div className="grid grid-cols-2 lg:grid-cols-3 gap-3">
            {players.map(player => (
              <div
                key={player.id}
                draggable
                onDragStart={(e) => onDragStart(e, player)}
                className="group p-4 border dark:border-dark-border rounded-lg cursor-grab active:cursor-grabbing hover:shadow-md hover:border-primary-300 dark:hover:border-primary-600 transition-all duration-200 bg-white dark:bg-dark-paper hover:bg-gray-50 dark:hover:bg-dark-light"
              >
                {/* Avatar e Nome */}
                <div className="flex flex-col items-center text-center mb-3">
                  <div className="w-12 h-12 rounded-full bg-primary-100 dark:bg-primary-900/20 flex items-center justify-center text-lg font-bold text-primary-600 dark:text-primary-400 mb-2 group-hover:scale-105 transition-transform">
                    {player.nome.charAt(0).toUpperCase()}
                  </div>
                  <p className="font-medium text-sm leading-tight">{player.nome}</p>
                </div>

                {/* Posição */}
                <div className="flex justify-center mb-3">
                  <span className={`text-xs px-3 py-1 rounded-full capitalize font-medium ${getPositionColor(player.posicao)}`}>
                    {player.posicao}
                  </span>
                </div>

                {/* Nível (Estrelas) */}
                <div className="flex justify-center gap-1 mb-2">
                  {Array.from({ length: 5 }).map((_, i) => (
                    <Star 
                      key={i} 
                      className={`w-3 h-3 ${
                        i < player.nivel 
                          ? 'text-yellow-400 fill-current' 
                          : 'text-gray-300 dark:text-gray-600'
                      }`} 
                    />
                  ))}
                </div>

                {/* Indicador de arrastar */}
                <div className="text-center">
                  <p className="text-xs text-gray-400 dark:text-gray-500 group-hover:text-primary-500 transition-colors">
                    {showDropZone ? 'Arraste ou solte aqui' : 'Arraste para o campo'}
                  </p>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default PlayerList;